import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Subscription, firstValueFrom } from 'rxjs';

import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { billingIntervalRankAnnualFirst } from '../../../../../common/enums/billing-interval-label.enum';
import { ProMessage } from '../../../../../common/enums/pro-feature-message.enum';
import { ToastLife } from '../../../../../common/enums/toast-life.enum';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { CountryAvailabilityService } from '../../../../../common/services/country-availability/country-availability.service';
import { PlatformService } from '../../../../../common/services/platform/platform.service';
import { MessageService } from '../../../service/message.service';
import { CheckoutService } from '../../services/checkout.service';
import {
  ProGateService,
  PaywallRequest,
  PurchaseUnavailableNotice,
} from '../../services/pro-gate.service';
import { PurchaseUnavailableReason } from '../../services/purchase-availability.service';
import { SubscriptionService } from '../../services/subscription.service';
import { PaywallModalComponent } from '../paywall-modal/paywall-modal.component';

/**
 * Always-mounted, headless host (one instance, in AppComponent). It exists so a
 * root service (`ProGateService`, which cannot inject `ModalController` here) can
 * still drive a modal: it subscribes to paywall requests and presents the modal,
 * and resumes a guest's Pro intent (checkout) once they finish signing up.
 */
@Component({
  selector: 'app-paywall-host',
  standalone: true,
  template: '',
})
@ComponentAccess('PaywallHostComponent')
export class PaywallHostComponent implements OnInit, OnDestroy {
  private readonly modalCtrl = inject(ModalController);
  private readonly proGate = inject(ProGateService);
  private readonly checkout = inject(CheckoutService);
  private readonly subscription = inject(SubscriptionService);
  private readonly auth = inject(AuthenticationService);
  private readonly platform = inject(PlatformService);
  private readonly countryAvailability = inject(CountryAvailabilityService);
  private readonly messageService = inject(MessageService);

  private readonly subs = new Subscription();
  private presenting = false;
  private wasAuthed = false;

  ngOnInit(): void {
    this.subs.add(
      this.proGate.requests$.subscribe((req) => void this.present(req)),
    );
    // Purchasing switched off for this channel (ADR-0173): a neutral toast in
    // place of the paywall. A toast rather than a modal on purpose — a
    // paywall-shaped sheet with nothing to buy is what earns "we were unable to
    // locate the in-app purchases", and a toast has no room for a CTA or a link
    // even if someone later tries to add one.
    this.subs.add(
      this.proGate.notices$.subscribe((notice) => this.showNotice(notice)),
    );
    this.subs.add(
      this.auth.isAuthenticated().subscribe((authed) => {
        const was = this.wasAuthed;
        this.wasAuthed = authed;
        if (authed && !was) void this.resumeProIntent();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * The compliance message, in place of a paywall. Info severity, no title, no
   * action: it states what the feature needs and stops there.
   */
  private showNotice(notice: PurchaseUnavailableNotice): void {
    this.messageService.add({
      severity: 'info',
      detail: this.noticeDetail(notice.reason),
      life: ToastLife.MEDIUM,
    });
  }

  private noticeDetail(reason: PurchaseUnavailableReason | null): string {
    switch (reason) {
      case PurchaseUnavailableReason.REGION_NOT_RELEASED:
        return ProMessage.SubscriptionsNotInRegion;
      case PurchaseUnavailableReason.ACCOUNT_NOT_ELIGIBLE:
        return ProMessage.SubscriptionsUnavailableOnAccount;
      default:
        return ProMessage.ProSubscriptionRequired;
    }
  }

  private async present(req: PaywallRequest): Promise<void> {
    // Last line of defence (ADR-0173). ProGateService already refuses to emit a
    // request when purchasing is off, so reaching here means a new caller pushed
    // one directly — one line here means no such caller can ever surface a price
    // inside a silenced store.
    if (!this.proGate.purchasingEnabled()) return;
    if (this.presenting) return; // never stack paywalls
    this.presenting = true;
    const modal = await this.modalCtrl.create({
      component: PaywallModalComponent,
      componentProps: { feature: req.feature, state: req.state },
      cssClass: 'paywall-modal',
    });
    void modal.onDidDismiss().then(() => {
      this.presenting = false;
    });
    await modal.present();
  }

  /**
   * After a guest taps a Pro CTA we route them to sign-up/-in with a stored
   * intent; once they're authenticated we resume automatically — so account +
   * payment feel like one flow:
   *   • WEB    → auto-launch checkout (no purchase UI needed).
   *   • NATIVE → re-open the paywall (now signed-in "needs plan" state) so they
   *     land back on Go Premium as intended. Native never auto-buys — the store
   *     purchase must go through the paywall's own UI (Apple 3.1.1 / Play).
   */
  private async resumeProIntent(): Promise<void> {
    if (!this.proGate.hasIntent()) return;
    // A guest can tap Go Premium, sign up, and land here AFTER the channel was
    // switched off (ADR-0173). Drop the intent rather than resuming: on web this
    // would auto-launch a checkout nobody may pay through, and on native it would
    // re-open the paywall the switch exists to suppress.
    if (!this.proGate.purchasingEnabled()) {
      this.proGate.clearIntent();
      return;
    }
    // They might already be Pro after signing in — re-check before doing anything.
    const isPro = await this.subscription.refresh();
    if (isPro) {
      this.proGate.clearIntent();
      return;
    }
    const intent = this.proGate.consumeIntent();
    if (!intent) return;
    if (this.platform.isNative()) {
      // Re-present the paywall for the feature they wanted (needPlan state).
      // Native never auto-buys (Apple 3.1.1 / Play): current store terms and
      // fresh consent must be shown immediately before the store sheet.
      this.proGate.open(intent.feature);
      return;
    }
    // Where the buyer is NOW — not where they were when they picked the plan.
    // A guest can pick under one country's pricing and finish signing up under
    // another's, and auto-buying then charges a price they were never shown
    // (ADR-0179). Re-open the paywall instead: one extra screen against a wrong
    // charge and a refund.
    let country: string | null = null;
    try {
      const availability = await firstValueFrom(
        this.countryAvailability.getAvailability(true),
      );
      country = availability.effectiveCountry;
    } catch {
      // Country unknown — treat the intent as stale rather than guess.
    }
    if (!this.proGate.isIntentFresh(intent, country)) {
      this.proGate.open(intent.feature);
      return;
    }

    try {
      // Buy exactly the plan the guest chose on the paywall. Only re-derive a
      // default (first eligible) when the intent carried no plan.
      let planId = intent.planId;
      if (!planId) {
        // Default to the annual plan (year first), matching the plan the paywall
        // pre-selects (ADR-0129), not the incidental API order.
        const plans = [...(await this.checkout.eligiblePlans())].sort(
          (a, b) =>
            billingIntervalRankAnnualFirst(a.billingInterval, b.billingInterval),
        );
        planId = plans[0]?.id;
      }
      if (planId) await this.checkout.purchase(planId);
    } catch {
      // Leave them on the page; they can retry from the paywall.
    }
  }
}
